import { Fix64 } from "../maths/fix64";
import { EliteRule, EnemyAffixDef, RARITY_IDS, Rarity } from "./balance";
import { EventModesData, parseEventModes } from "./eventModes";
import { SupplyId, isSupplyId } from "./supplies";

/**
 * Events and the endgame's data (FINISH_PLAN WP6, DESIGN §10.5), parsed from the generated bundle's `events` section
 * (balance/src/events.json): the dated event calendar and the weekly rotation's kinds, each with its free reward track
 * and its ember sources, the Weekly Trial and the Heroic tier. The rules that read it live in meta/events.ts,
 * meta/eventSchedule.ts (which event runs on a day), meta/embers.ts (what a signal pays) and meta/trials.ts.
 */

/** What a track node, the Weekly Trial or a Heroic clear pays. Every part is optional (0, null or ""). */
export interface EventReward {
  /** Average Win chests of resources at the player's highest cleared level (meta/orders.ts chestShare). */
  readonly resources: Fix64;
  readonly speedUpMinutes: number;
  readonly gems: number;
  readonly marks: number;
  /** MP-23: Trophies, the Trophy Hall's earned currency (never in a paid grant: gen.py); 0 or absent for none. */
  readonly trophies?: number;
  /** A chest of this rarity at the player's highest cleared level, gear included; null for none. */
  readonly chest: Rarity | null;
  /** A flag-emblem variant id the Skins package applies ("" for none). */
  readonly emblem: string;
  /** Forge Essence (gear v2, docs/GEAR_V2.md); 0 for none. */
  readonly essence: number;
  /** Fusion Sigils of this rarity (each stands in for one fodder item in a fusion of it); null for none. */
  readonly sigil: Rarity | null;
  /** How many sigils (1 when `sigil` is set and this is absent). */
  readonly sigils: number;
  /** MP-06: a pre-battle supply (`supply`, absent: none) and how many (`supplies`, 1 when absent). */
  readonly supply?: SupplyId;
  readonly supplies?: number;
}

export interface EventNode {
  /** Embers collected (in total, never spent) that open the node. */
  readonly embers: number;
  readonly reward: EventReward;
}

/**
 * One way to earn an event's embers (MP-21, events.json `_sources`): a signal counted n times pays
 * floor(n / per) × amount, at most `runCap` from one battle or one hub sync and `cap` over the whole event (0 = no limit).
 */
export interface EmberSource {
  /** One of meta/embers.ts EMBER_SIGNALS. */
  readonly signal: string;
  readonly amount: number;
  readonly per: number;
  readonly runCap: number;
  readonly cap: number;
}

/** Hunters' Week's quarry: the enemy whose kills pay (`featuredKill`), and how many kills make one `amount`. */
export interface FeaturedEnemy { readonly enemy: string; readonly per: number }

/** How an event looks: its banner/chip tint and its picture (`icon:<painted icon>`, `portrait:<id>[:<tier>]`, `featured`). */
export interface EventLook { readonly theme: string; readonly art: string }

export interface LiveEventData extends EventLook {
  /** A dated event's id, or a rotation week's instance id `<kind>_<ISO year>w<ISO week>` (saves key progress by it). */
  readonly id: string;
  /** The rotation kind ("proving", "siege" …), or "" for a dated event. */
  readonly kind: string;
  readonly name: string;
  /** The event currency's display name ("Embers"). */
  readonly currency: string;
  readonly blurb: string;
  /** UTC day numbers (days since 1970-01-01): the first day, and the day after the last (gen.py from the dates). */
  readonly startDay: number;
  readonly endDay: number;
  readonly nodes: readonly EventNode[];
  /** What earns its embers. */
  readonly sources: readonly EmberSource[];
  /** The week's featured enemy (Hunters' Week), else null. */
  readonly featured: FeaturedEnemy | null;
  /** One week of the rotation: its saved progress is pruned once long over, and its nodes pay the Event shop's Marks. */
  readonly recurring: boolean;
}

/** A kind of the weekly rotation (events.json `rotation.kinds`). */
export interface RotationKind extends EventLook {
  readonly id: string;
  readonly name: string;
  readonly currency: string;
  readonly blurb: string;
  readonly nodes: readonly EventNode[];
  readonly sources: readonly EmberSource[];
  /** Hunters' Week's quarry list, taken in turn (empty for the other kinds). */
  readonly featured: readonly FeaturedEnemy[];
}

/** The weekly rotation: from the Monday `fromDay`, week w runs kinds[w mod kinds]; a week clipped under minDays is dropped. */
export interface RotationData {
  readonly fromDay: number;
  readonly minDays: number;
  readonly kinds: readonly RotationKind[];
}

export interface TrialModifierData {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  readonly enemyHpMult: Fix64;
  /** Replaces the level's elite rules (empty: none). */
  readonly elites: readonly EliteRule[];
}

export interface TrialData {
  readonly unlockAfterLevel: number;
  /** The lowest level the Trial ever features. */
  readonly minLevel: number;
  readonly reward: EventReward;
  readonly modifiers: readonly TrialModifierData[];
}

export interface HeroicData {
  readonly enemyHpMult: Fix64;
  readonly marksBase: number;
  readonly marksPerLevel: number;
  /** MP-23: Trophies on a level's first Heroic win (its Heroic star); 0 in an older bundle. */
  readonly trophies: number;
}

export interface EventsData {
  /** Events count and show only once this level is cleared. */
  readonly unlockAfterLevel: number;
  /** The classic ember sources: a dated event's unless it lists its own. */
  readonly sources: readonly EmberSource[];
  /** The dated set-piece events, in date order, never overlapping (gen.py). The rotation fills the weeks between. */
  readonly calendar: readonly LiveEventData[];
  /** The weekly rotation (null: no events between the dated ones). */
  readonly rotation: RotationData | null;
  readonly trial: TrialData;
  readonly heroic: HeroicData;
  /** The weekly event modes (MP-21): Boss Week, Race Week, the Odyssey (data/eventModes.ts); absent or null: none. */
  readonly modes?: EventModesData | null;
}

export const NO_REWARD: EventReward = { resources: Fix64.Zero, speedUpMinutes: 0, gems: 0, marks: 0, trophies: 0, chest: null, emblem: "", essence: 0, sigil: null, sigils: 0 };

/** No events data (an old bundle, a test fixture): no calendar, and the Trial and Heroic tiers never open. */
export function emptyEvents(): EventsData {
  return {
    unlockAfterLevel: Number.MAX_SAFE_INTEGER,
    sources: [],
    calendar: [],
    rotation: null,
    trial: { unlockAfterLevel: Number.MAX_SAFE_INTEGER, minLevel: 1, reward: NO_REWARD, modifiers: [] },
    heroic: { enemyHpMult: Fix64.One, marksBase: 0, marksPerLevel: 0, trophies: 0 },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  throw new TypeError(`expected integer, got ${JSON.stringify(v)}`);
}
function str(v: Json): string {
  if (typeof v !== "string") throw new TypeError(`expected string, got ${JSON.stringify(v)}`);
  return v;
}
function req(o: Json, key: string): Json {
  if (o === null || typeof o !== "object" || !(key in o)) throw new Error(`Missing JSON member '${key}'`);
  return o[key];
}
function opt(o: Json, key: string, fallback: Json): Json { return o !== null && typeof o === "object" && key in o ? o[key] : fallback; }

function rarityOrNull(v: Json, what: string): Rarity | null {
  if (v === null) return null;
  const r = (RARITY_IDS as readonly string[]).indexOf(str(v));
  if (r < 0) throw new Error(`unknown ${what} rarity '${v}'`);
  return r as Rarity;
}

export function parseReward(r: Json): EventReward {
  const sigil = rarityOrNull(opt(r, "sigil", null), "sigil");
  return {
    resources: Fix64.parse(String(opt(r, "resources", "0"))), speedUpMinutes: int(opt(r, "speedUp", 0)),
    gems: int(opt(r, "gems", 0)), marks: int(opt(r, "marks", 0)), trophies: int(opt(r, "trophies", 0)), chest: rarityOrNull(opt(r, "chest", null), "chest"), emblem: str(opt(r, "emblem", "")),
    essence: int(opt(r, "essence", 0)), sigil, sigils: sigil === null ? 0 : int(opt(r, "sigils", 1)),
    ...parseRewardSupply(r),
  };
}

/** A reward's optional pre-battle supply (MP-06): `supply` and `supplies` (default 1). */
function parseRewardSupply(r: Json): { supply?: SupplyId; supplies?: number } {
  const v = opt(r, "supply", undefined);
  if (v === undefined) return {};
  if (!isSupplyId(v)) throw new Error(`reward: unknown supply '${String(v)}'`);
  const n = int(opt(r, "supplies", 1));
  if (n < 1) throw new Error("reward: supplies at least 1");
  return { supply: v, supplies: n };
}

function parseElites(list: Json[], affixes: readonly EnemyAffixDef[], where: string): EliteRule[] {
  return list.map((r) => {
    const affix = affixes.findIndex((a) => a.id === str(req(r, "affix")));
    if (affix < 0) throw new Error(`${where}: unknown affix '${r.affix}'`);
    const every = int(req(r, "every")), fromStage = int(opt(r, "fromStage", 1));
    if (every < 1 || fromStage < 1) throw new Error(`${where}: elite rule needs every >= 1 and fromStage >= 1`);
    return { affix, every, fromStage };
  });
}

function parseSources(list: Json[]): EmberSource[] {
  return list.map((x) => ({ signal: str(req(x, "signal")), amount: int(req(x, "amount")), per: Math.max(1, int(opt(x, "per", 1))), runCap: int(opt(x, "runCap", 0)), cap: int(opt(x, "cap", 0)) }));
}
const parseNodes = (list: Json[]): EventNode[] => list.map((n) => ({ embers: int(req(n, "embers")), reward: parseReward(req(n, "reward")) }));
const parseLook = (o: Json): EventLook => ({ theme: str(opt(o, "theme", "ember")), art: str(opt(o, "art", "icon:eventEmber")) });

function parseRotation(r: Json): RotationData | null {
  if (r === null || r === undefined) return null;
  const kinds = (req(r, "kinds") as Json[]).map((k): RotationKind => ({
    id: str(req(k, "id")), name: str(req(k, "name")), currency: str(req(k, "currency")), blurb: str(opt(k, "blurb", "")), ...parseLook(k),
    nodes: parseNodes(req(k, "nodes")), sources: parseSources(req(k, "sources")),
    featured: (opt(k, "featured", []) as Json[]).map((f) => ({ enemy: str(req(f, "enemy")), per: Math.max(1, int(req(f, "per"))) })),
  }));
  return kinds.length === 0 ? null : { fromDay: int(req(r, "fromDay")), minDays: int(req(r, "minDays")), kinds };
}

/** Reads the `events` section from the bundle root; a bundle without one gets `emptyEvents`. */
export function parseEvents(root: Json, affixes: readonly EnemyAffixDef[]): EventsData {
  const e = opt(root, "events", undefined);
  if (e === undefined) return emptyEvents();
  const tr = req(e, "trial"), he = req(e, "heroic");
  const sources = parseSources(req(e, "sources"));
  return {
    unlockAfterLevel: int(req(e, "unlockAfterLevel")),
    sources,
    calendar: (req(e, "calendar") as Json[]).map((c) => ({
      id: str(req(c, "id")), kind: "", name: str(req(c, "name")), currency: str(req(c, "currency")), blurb: str(opt(c, "blurb", "")), ...parseLook(c),
      startDay: int(req(c, "startDay")), endDay: int(req(c, "endDay")), nodes: parseNodes(req(c, "nodes")),
      sources: c.sources === undefined ? sources : parseSources(c.sources), featured: null, recurring: false,
    })),
    rotation: parseRotation(opt(e, "rotation", null)),
    trial: {
      unlockAfterLevel: int(req(tr, "unlockAfterLevel")), minLevel: int(req(tr, "minLevel")), reward: parseReward(req(tr, "reward")),
      modifiers: (req(tr, "modifiers") as Json[]).map((m) => ({
        id: str(req(m, "id")), name: str(req(m, "name")), blurb: str(opt(m, "blurb", "")), enemyHpMult: Fix64.parse(String(req(m, "enemyHpMult"))),
        elites: parseElites(opt(m, "elites", []) as Json[], affixes, `trial modifier ${m.id}`),
      })),
    },
    heroic: { enemyHpMult: Fix64.parse(String(req(he, "enemyHpMult"))), marksBase: int(req(he, "marksBase")), marksPerLevel: int(req(he, "marksPerLevel")), trophies: int(opt(he, "trophies", 0)) },
    modes: parseEventModes(opt(e, "modes", null)),
  };
}
